import { getFontFamilyCss, getTitleFontSizeCss, getSubtitleFontSizeCss, getDescriptionFontSizeCss } from '../../utils/fontHelper';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceProductCard from '../ecommerce/EcommerceProductCard';
import EcommerceProductDetail from '../ecommerce/EcommerceProductDetail';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import UnifiedHeader, { DEFAULT_SECONDARY_NAVBAR } from '../common/UnifiedHeader';
import StoreInSetup from '../common/StoreInSetup';
import UnifiedFooter from '../common/UnifiedFooter';
import UnifiedHeroBanner, { DEFAULT_UNIFIED_BANNERS, DEFAULT_SECONDARY_BANNERS } from '../common/UnifiedHeroBanner';
import { FaqSectionBlock, DEFAULT_FAQ_SECTION } from './FaqPage';
import { useWebStore } from '../../context/WebStoreContext';
import { applyEcommerceTheme, isWebOrStoreRoute, resetSystemTheme } from '../../utils/ecommerceHelper';
import { getImageCandidateUrls } from '../../utils/imageHelper';

export const DEFAULT_HOME_SECTIONS = [
  { id: 'hero', name: 'Banners Principales (Hero Slider)', icon: 'bi-images', description: 'Carrusel principal con imágenes de portada y llamados a la acción', enabled: true },
  { id: 'benefits', name: 'Barra de Beneficios (Pilares 1)', icon: 'bi-shield-check', description: '4 pilares comerciales (Envíos, Fábrica directa, Mayorista/Minorista, WhatsApp)', enabled: true },
  { id: 'categoryShortcuts', name: 'Líneas de Productos Destacadas', icon: 'bi-grid-3x3-gap-fill', description: 'Tarjetas fotográficas con accesos rápidos a rubros principales', enabled: true },
  { id: 'bestSellers', name: '¡Los Más Vendidos! (Ranking)', icon: 'bi-fire', description: 'Grilla de productos más vendidos seleccionados para destacar en portada y tienda', enabled: false },
  { id: 'featuredProducts', name: 'Artículos Destacados / Catálogo', icon: 'bi-star-fill', description: 'Grilla de productos más solicitados con compra directa', enabled: true },
  { id: 'benefitsSecondary', name: 'Segunda Barra de Pilares (Pilares 2)', icon: 'bi-shield-plus', description: 'Segunda barra de pilares y diferenciales comerciales para ubicar más abajo', enabled: false },
  { id: 'secondaryBanner', name: 'Banners Secundarios (Envíos & Servicios)', icon: 'bi-truck', description: 'Banner fotográfico intermedio para destacar envíos a todo el país, logística o servicios con fotos y texto', enabled: true },
  { id: 'promoBanners', name: 'Banners de Oportunidades', icon: 'bi-tag-fill', description: 'Banners dobles para promociones especiales y líneas de temporada', enabled: true },
  { id: 'institutional', name: 'Institucional / Sobre la Empresa', icon: 'bi-building-check', description: 'Teaser institucional de planta industrial y trayectoria', enabled: true },
  { id: 'faq', name: 'Preguntas Frecuentes (FAQ)', icon: 'bi-patch-question-fill', description: 'Acordeón interactivo con respuestas a las consultas más habituales', enabled: false }
];

export const DEFAULT_NAVBAR_ITEMS = [
  { id: 'home', label: 'INICIO', path: '/', description: 'Página principal de la tienda', icon: 'bi-house-door', enabled: true },
  { id: 'tienda', label: 'TIENDA', path: '/tienda', description: 'Catálogo de productos y compra online', icon: 'bi-shop', enabled: true },
  { id: 'novedades', label: 'NOVEDADES', path: '/novedades', description: 'Lanzamientos y ofertas especiales', icon: 'bi-megaphone', enabled: true },
  { id: 'descargas', label: 'DESCARGAS', path: '/descargas', description: 'Catálogos y listas de precios en PDF', icon: 'bi-cloud-arrow-down', enabled: true },
  { id: 'empresa', label: 'EMPRESA', path: '/empresa', description: 'Historia y planta industrial', icon: 'bi-building', enabled: true },
  { id: 'donde-estamos', label: 'DONDE ESTAMOS', path: '/donde-estamos', description: 'Ubicación de fábrica y sucursales', icon: 'bi-geo-alt', enabled: true },
  { id: 'faq', label: 'PREGUNTAS FRECUENTES', path: '/preguntas-frecuentes', description: 'Centro de ayuda y dudas frecuentes', icon: 'bi-question-circle', enabled: false },
  { id: 'contacto', label: 'CONTACTO', path: '/contacto', description: 'Formulario de contacto y WhatsApp', icon: 'bi-telephone', enabled: true }
];

const resolvePilarFontSize = (val, defaultVal) => {
  if (!val) return defaultVal;
  if (val === '2xs' || val === 'xxs') return '0.68rem';
  if (val === 'xs' || val === 'xsmall') return '0.75rem';
  if (val === 'sm' || val === 'small') return '0.84rem';
  if (val === 'md' || val === 'medium') return '0.95rem';
  if (val === 'lg' || val === 'large') return '1.10rem';
  if (val === 'xl' || val === 'xlarge') return '1.25rem';
  if (val === 'xxl' || val === '2xl') return '1.45rem';
  return val;
};

const resolvePilarFontWeight = (val, defaultVal) => {
  const map = { normal: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800, black: 900 };
  return map[val] || val || defaultVal;
};

const DEFAULT_LANDING_CONFIG = {
  company_id: 1,
  colors: {
    primaryColor: '#0d6efd',
    secondaryColor: '#0a58ca',
    accentColor: '#ff6b35',
    buttonColor: '#ffc107',
    buttonPrimaryText: '#111827',
    buttonSecondaryBg: '#f8fafc',
    buttonSecondaryText: '#1e293b',
    textColor: '#1e293b',
    pageBg: '#f8fafc',
    cardBg: '#ffffff',
    borderColor: '#e2e8f0',
    borderRadius: '12px',
    topBarBg: '#0f172a',
    topBarText: '#ffffff',
    navbarBg: '#ffffff',
    headerText: '#1e293b',
    headerBorder: '#e2e8f0',
    footerBg: '#1e293b',
    footerTextColor: '#94a3b8',
    footerTitleColor: '#ffffff',
    footerBorderColor: '#334155'
  },
  homeSections: DEFAULT_HOME_SECTIONS,
  navbarItems: DEFAULT_NAVBAR_ITEMS,
  carouselSettings: {
    sizePreset: 'standard',
    widthMode: 'container',
    fitMode: 'cover',
    borderRadius: '16px'
  },
  topBar: {
    enabled: true,
    announcementText: '🚀 ¡Venta Mayorista y Minorista con Envíos a Todo el País! | Consultas por WhatsApp',
    phone: '(011) 4247-6622',
    whatsapp: '5491142476622',
    email: 'ventas@hiperpel.com.ar'
  },
  heroBanners: DEFAULT_UNIFIED_BANNERS,
  benefits: [
    {
      id: 'b-1',
      icon: 'bi-truck',
      title: 'Envíos a Todo el País',
      description: 'Logística rápida y segura a todas las provincias',
      extraText: 'Despacho rápido garantizado'
    },
    {
      id: 'b-2',
      icon: 'bi-boxes',
      title: 'Fabricantes Directos',
      description: 'Precios de fábrica en cartón y embalajes',
      extraText: 'Venta directa sin intermediarios'
    },
    {
      id: 'b-3',
      icon: 'bi-tags-fill',
      title: 'Mayorista y Minorista',
      description: 'Escalas por cantidad y listas especiales',
      extraText: 'Precios especiales por volumen'
    },
    {
      id: 'b-4',
      icon: 'bi-whatsapp',
      title: 'Atención Personalizada',
      description: 'Asesoramiento ágil directo por WhatsApp',
      extraText: 'Respuesta inmediata de asesores'
    }
  ],
  benefitsSettings: {
    fontFamily: 'inherit',
    line1FontFamily: 'inherit',
    line1Color: '#1e293b',
    line1FontSize: 'md',
    line1FontWeight: 'bold',
    line2FontFamily: 'inherit',
    line2Color: '#64748b',
    line2FontSize: 'sm',
    line2FontWeight: 'normal',
    line3FontFamily: 'inherit',
    line3Color: '#0d6efd',
    line3FontSize: 'xs',
    line3FontWeight: 'medium'
  },
  benefitsSecondary: [
    {
      id: 'b2-1',
      icon: 'bi-clock-history',
      title: 'Despacho en 24/48hs',
      description: 'Preparación ágil de pedidos para entrega rápida',
      extraText: 'Envíos a todo el país'
    },
    {
      id: 'b2-2',
      icon: 'bi-credit-card',
      title: 'Múltiples Medios de Pago',
      description: 'Transferencias, tarjetas y facturación directa',
      extraText: 'Facturación directa A y B'
    },
    {
      id: 'b2-3',
      icon: 'bi-patch-check',
      title: 'Garantía de Calidad',
      description: 'Materiales testeados de máxima resistencia',
      extraText: 'Directo de nuestra fábrica'
    },
    {
      id: 'b2-4',
      icon: 'bi-chat-dots',
      title: 'Cotizaciones Especiales',
      description: 'Presupuestos personalizados por cantidad y volumen',
      extraText: 'Asesoramiento por WhatsApp'
    }
  ],
  benefitsSecondarySettings: {
    fontFamily: 'inherit',
    line1FontFamily: 'inherit',
    line1Color: '#1e293b',
    line1FontSize: 'md',
    line1FontWeight: 'bold',
    line2FontFamily: 'inherit',
    line2Color: '#64748b',
    line2FontSize: 'sm',
    line2FontWeight: 'normal',
    line3FontFamily: 'inherit',
    line3Color: '#0d6efd',
    line3FontSize: 'xs',
    line3FontWeight: 'medium'
  },
  categoryShortcuts: {
    enabled: true,
    columnsPerRow: 5,
    cardStyle: 'strip',
    cardHeight: 'large',
    showCardCta: false,
    chevronColor: '#16a34a',
    imageAreaBg: '#ededed',
    imageFit: 'cover',
    sectionTitle: 'Líneas de Productos Destacadas',
    sectionSubtitle: 'Accedé de forma directa a nuestros rubros principales con stock disponible',
    fontFamily: 'inherit',
    cardTitleLineHeight: '1.25',
    cardSubtitleLineHeight: '1.35',
    cards: [
      {
        id: 'card-1',
        title: 'Bolsas y Polietileno',
        subtitle: 'Arranque, camiseta, riñón y consorcio',
        image: '/images/banners/Frente-Web1.jpg',
        rubroTarget: '',
        link: '/tienda?rubro=Bolsas',
        order: 1,
        is_active: true
      },
      {
        id: 'card-2',
        title: 'Cajas y Embalaje',
        subtitle: 'Cajas de cartón, corrugado, cintas y film',
        image: '/images/banners/Caja.jpg',
        rubroTarget: '',
        link: '/tienda?rubro=Embalaje',
        order: 2,
        is_active: true
      },
      {
        id: 'card-3',
        title: 'Línea Gastronómica',
        subtitle: 'Bandejas, blondas, descartables y aluminio',
        image: '/images/banners/Frente-Web2.jpg',
        rubroTarget: '',
        link: '/tienda?rubro=Gastronomia',
        order: 3,
        is_active: true
      },
      {
        id: 'card-4',
        title: 'Papelería y Resmas',
        subtitle: 'Bobinas, papeles para envolver y librería',
        image: '/images/banners/Slide4.jpg',
        rubroTarget: '',
        link: '/tienda?rubro=Papeleria',
        order: 4,
        is_active: true
      }
    ]
  },
  secondaryBanners: DEFAULT_SECONDARY_BANNERS,
  secondaryCarouselSettings: {
    sizePreset: 'standard',
    widthMode: 'container',
    fitMode: 'cover',
    borderRadius: '16px',
    autoPlay: true,
    interval: 6000,
    fontFamily: 'inherit'
  },
  featuredProductsSection: {
    enabled: true,
    title: 'Artículos Destacados',
    subtitle: 'Los productos más solicitados de nuestro catálogo con entrega inmediata',
    titleColor: '#1e293b',
    subtitleColor: '#64748b',
    buttonText: 'Ver Catálogo Completo',
    showButton: true,
    limit: 8
  },
  bestSellersSection: {
    enabled: true,
    badgeText: '🔥 Ranking de Ventas',
    title: '¡Los Más Vendidos!',
    subtitle: 'Los productos más elegidos por nuestros clientes mayoristas y minoristas',
    selectionMode: 'manual',
    selectedProducts: [],
    selectedRubros: [],
    limit: 8,
    columnsPerRow: 4,
    imageFit: 'contain',
    imageHeight: 'standard'
  },
  secondaryNavBar: DEFAULT_SECONDARY_NAVBAR,
  faqSection: DEFAULT_FAQ_SECTION,
  promoBannersSettings: {
    showSectionTitle: true,
    title: 'Oportunidades & Promociones Destacadas',
    subtitle: 'Aprovechá ofertas por cantidad, lanzamientos y productos en liquidación',
    badgeText: 'Promociones Especiales',
    titleColor: '#1e293b',
    subtitleColor: '#64748b',
    cardStyle: 'overlay', // 'overlay' | 'below'
    columnsPerRow: 2,
    bannerHeight: 'standard',
    showGradient: true,
    backgroundColor: '#f8fafc',
    fontFamily: 'inherit'
  },
  promoBanners: [
    {
      id: 'promo-1',
      title: 'Cajas de Pizza & Empanadas',
      subtitle: 'Microcorrugado de alta resistencia con o sin impresión.',
      buttonText: 'Ver Opciones',
      showButton: true,
      rubroTarget: '',
      link: '/tienda?rubro=Cajas',
      titleColor: '',
      subtitleColor: '',
      cardStyle: 'inherit',
      image: '/images/banners/Caja.jpg',
      is_active: true
    },
    {
      id: 'promo-2',
      title: 'Papeles & Rollos Térmicos',
      subtitle: 'Para puntos de venta, básculas e impresoras fiscales.',
      buttonText: 'Ver Línea',
      showButton: true,
      rubroTarget: '',
      link: '/tienda?rubro=Papeleria',
      titleColor: '',
      subtitleColor: '',
      cardStyle: 'inherit',
      image: '/images/banners/Slide4.jpg',
      is_active: true
    }
  ],
  aboutSection: {
    enabled: true,
    badgeText: 'Fábrica Directa',
    homeTitle: 'Más de 30 años liderando en cartón, envases y embalajes',
    title: 'Sobre Nuestra Empresa',
    tagline: 'Más de 30 años de experiencia e innovación en el rubro',
    paragraph1: 'Somos una empresa argentina dedicada a la fabricación y comercialización integral de soluciones de embalaje, papelería comercial y descartables para todo el país.',
    paragraph2: 'Brindamos atención personalizada tanto a comercios minoristas como a industrias y grandes distribuidores de todo el país, garantizando calidad superior, precios competitivos y entregas puntuales.',
    image: '/images/banners/Frente-Web1.jpg',
    button1Text: 'Conocer Nuestra Empresa',
    button1Link: '/empresa',
    button2Text: 'Solicitar Asesoramiento',
    button2Link: '/contacto',
    floatingBadgeShow: true,
    floatingBadgeTitle: 'Calidad & Stock Permanente',
    floatingBadgeSubtitle: 'Despachos rápidos a toda la Argentina',
    backgroundColor: '#ffffff',
    showGradient: false,
    stats: [
      { number: '30+', label: 'Años de Trayectoria' },
      { number: '1000+', label: 'Artículos en Catálogo' },
      { number: '24h', label: 'Despacho Rápido' },
      { number: '100%', label: 'Cobertura Nacional' }
    ]
  }
};

const CategoryShortcutCard = ({ card, sectionConfig, colsNum }) => {
  const candidateUrls = useMemo(() => (card?.image ? getImageCandidateUrls(card.image) : []), [card?.image]);
  const [candidateIdx, setCandidateIdx] = useState(0);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setCandidateIdx(0);
    setHasError(false);
  }, [card?.image]);

  useEffect(() => {
    if (!candidateUrls.length || hasError) return;
    const currentCandidate = candidateUrls[candidateIdx];
    if (!currentCandidate) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {};
    img.onerror = () => {
      if (cancelled) return;
      if (candidateIdx + 1 < candidateUrls.length) {
        setCandidateIdx(prev => prev + 1);
      } else {
        setHasError(true);
      }
    };
    img.src = currentCandidate;
    return () => {
      cancelled = true;
    };
  }, [candidateUrls, candidateIdx, hasError]);

  const resolvedImgUrl = !hasError && candidateUrls.length > 0 ? candidateUrls[candidateIdx] : null;
  const targetLink = card.link || (card.rubroTarget ? `/tienda?rubro=${encodeURIComponent(card.rubroTarget)}` : `/tienda?rubro=${encodeURIComponent(card.title || '')}`);

  const useGradient = sectionConfig?.showGradient !== false && card.showGradient !== false;
  const colorTheme = (card.textColorTheme && card.textColorTheme !== 'inherit')
    ? card.textColorTheme
    : (sectionConfig?.cardTextColorTheme || 'light');

  const titleColor = colorTheme === 'custom'
    ? (card.titleColor || sectionConfig?.cardTitleColor || '#ffffff')
    : colorTheme === 'dark'
    ? '#0f172a'
    : '#ffffff';

  const subtitleColor = colorTheme === 'custom'
    ? (card.subtitleColor || sectionConfig?.cardSubtitleColor || '#f1f5f9')
    : colorTheme === 'dark'
    ? '#334155'
    : 'rgba(248, 250, 252, 0.92)';

  const ctaColor = colorTheme === 'custom'
    ? (card.ctaColor || sectionConfig?.cardCtaColor || '#ffc107')
    : colorTheme === 'dark'
    ? 'var(--ecom-primary, #0d6efd)'
    : '#ffc107';

  const titleSizeKey = (card.titleFontSize && card.titleFontSize !== 'inherit')
    ? card.titleFontSize
    : (sectionConfig?.cardTitleFontSize || 'md');
  const titleFontSizeMap = colsNum >= 5
    ? { sm: '1rem', md: '1.2rem', lg: '1.4rem', xl: '1.6rem' }
    : { sm: '1.15rem', md: '1.5rem', lg: '1.8rem', xl: '2.1rem' };
  const resolvedTitleFontSize = titleFontSizeMap[titleSizeKey] || titleFontSizeMap.md;

  const subtitleSizeKey = (card.subtitleFontSize && card.subtitleFontSize !== 'inherit')
    ? card.subtitleFontSize
    : (sectionConfig?.cardSubtitleFontSize || 'md');
  const subtitleFontSizeMap = colsNum >= 5
    ? { sm: '0.72rem', md: '0.8rem', lg: '0.92rem' }
    : { sm: '0.78rem', md: '0.88rem', lg: '1.02rem' };
  const resolvedSubtitleFontSize = subtitleFontSizeMap[subtitleSizeKey] || subtitleFontSizeMap.md;

  const fontWeightMap = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900
  };
  const titleWeightKey = (card.titleFontWeight && card.titleFontWeight !== 'inherit')
    ? card.titleFontWeight
    : (sectionConfig?.cardTitleFontWeight || 'bold');
  const resolvedTitleFontWeight = fontWeightMap[titleWeightKey] || 700;

  const subtitleWeightKey = (card.subtitleFontWeight && card.subtitleFontWeight !== 'inherit')
    ? card.subtitleFontWeight
    : (sectionConfig?.cardSubtitleFontWeight || 'normal');
  const resolvedSubtitleFontWeight = fontWeightMap[subtitleWeightKey] || 400;

  const hAlign = (card.textAlignment && card.textAlignment !== 'inherit')
    ? card.textAlignment
    : (sectionConfig?.cardTextAlignment || 'left');
  const alignItemsClass = hAlign === 'center' ? 'align-items-center text-center' : hAlign === 'right' ? 'align-items-end text-end' : 'align-items-start text-start';

  const vAlign = (card.verticalAlignment && card.verticalAlignment !== 'inherit')
    ? card.verticalAlignment
    : (sectionConfig?.cardVerticalAlignment || 'bottom');
  const justifyClass = vAlign === 'top' ? 'justify-content-start' : vAlign === 'center' ? 'justify-content-center' : 'justify-content-end';

  const shadowMode = (card.textShadow && card.textShadow !== 'inherit')
    ? card.textShadow
    : (sectionConfig?.cardTextShadow || (colorTheme === 'dark' && !useGradient ? 'none' : 'subtle'));
  const textShadowCss = shadowMode === 'strong'
    ? (colorTheme === 'dark' ? '0 2px 10px rgba(255,255,255,0.9), 0 1px 3px rgba(255,255,255,0.95)' : '0 2px 10px rgba(0,0,0,0.85), 0 1px 4px rgba(0,0,0,0.9)')
    : shadowMode === 'subtle'
    ? (colorTheme === 'dark' ? '0 1px 4px rgba(255,255,255,0.75)' : '0 2px 6px rgba(0,0,0,0.55)')
    : 'none';

  const cardStyle = (card.cardStyle && card.cardStyle !== 'inherit')
    ? card.cardStyle
    : (sectionConfig?.cardStyle || 'strip');

  const cardHeightMode = card.cardHeight || sectionConfig?.cardHeight || 'large';
  const imageAreaHeight = cardHeightMode === 'large'
    ? (colsNum >= 5 ? '165px' : '185px')
    : (colsNum >= 5 ? '140px' : '155px');

  const chevronColor = card.chevronColor || sectionConfig?.chevronColor || '#16a34a';
  const imageAreaBg = card.imageAreaBg || sectionConfig?.imageAreaBg || '#ededed';
  const imageFit = card.imageFit || sectionConfig?.imageFit || 'cover';
  const isContain = imageFit === 'contain';

  const isStripStyle = cardStyle === 'strip';
  const showCta = isStripStyle
    ? Boolean(sectionConfig?.showCardCta === true && card.showCta !== false)
    : (sectionConfig?.showCardCta !== false && card.showCta !== false);

  const ctaText = (card.ctaText !== undefined && card.ctaText !== '')
    ? card.ctaText
    : (sectionConfig?.cardCtaText !== undefined ? sectionConfig.cardCtaText : 'Ver Artículos');

  const gradientDir = vAlign === 'top' ? 'to bottom' : 'to top';
  const overlayBg = useGradient
    ? `linear-gradient(${gradientDir}, rgba(15, 23, 42, 0.88) 0%, rgba(15, 23, 42, 0.38) 55%, rgba(15, 23, 42, 0.05) 100%)`
    : 'transparent';

  /* ===== ESTILO A: FICHA CON FRANJA INFERIOR (Estilo Catálogo solicitado por el cliente) ===== */
  if (isStripStyle) {
    return (
      <a
        href={targetLink}
        className="category-shortcut-card category-card-strip d-flex flex-column h-100 text-decoration-none overflow-hidden bg-white"
        style={{
          border: '1px solid #d1d5db',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease'
        }}
      >
        {/* Recuadro Superior de Imagen */}
        <div
          className="category-card-img-wrapper d-flex align-items-center justify-content-center position-relative overflow-hidden w-100"
          style={{
            height: imageAreaHeight,
            backgroundColor: imageAreaBg,
            borderBottom: '1px solid #d1d5db',
            padding: isContain ? '10px' : '0'
          }}
        >
          {resolvedImgUrl ? (
            <img
              src={resolvedImgUrl}
              alt={card.title || 'Rubro'}
              className="category-shortcut-img"
              style={{
                width: '100%',
                height: '100%',
                objectFit: isContain ? 'contain' : 'cover',
                objectPosition: 'center',
                display: 'block',
                transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
            />
          ) : (
            <div className="d-flex flex-column align-items-center justify-content-center text-muted py-3">
              <i className="bi bi-box-seam fs-1 opacity-50 mb-1"></i>
              <span className="small opacity-75">Sin imagen</span>
            </div>
          )}
        </div>

        {/* Franja Inferior con Texto */}
        <div
          className="category-card-text-strip d-flex flex-column flex-grow-1 bg-white"
          style={{
            padding: '12px 14px 14px 14px',
            minHeight: '92px',
            justifyContent: 'flex-start'
          }}
        >
          {/* Título con Chevron » Verde */}
          <h3
            className="category-strip-title mb-1 d-flex align-items-baseline"
            style={{
              fontSize: colsNum >= 5 ? '0.96rem' : resolvedTitleFontSize,
              fontWeight: 700,
              color: '#1e293b',
              lineHeight: card.titleLineHeight || sectionConfig?.cardTitleLineHeight || 1.25,
              minHeight: '2.55em'
            }}
          >
            <span
              className="fw-bold me-1 flex-shrink-0"
              style={{
                color: chevronColor,
                fontSize: '1.2em',
                lineHeight: 1,
                fontFamily: 'system-ui, -apple-system, sans-serif'
              }}
            >
              »
            </span>
            <span style={{ color: '#1e293b' }}>
              {card.title}
            </span>
          </h3>

          {/* Subtítulo / Descripción del Rubro */}
          {card.subtitle && (
            <p
              className="category-strip-subtitle mb-0"
              style={{
                color: '#64748b',
                fontSize: colsNum >= 5 ? '0.8rem' : resolvedSubtitleFontSize,
                fontWeight: 400,
                lineHeight: card.subtitleLineHeight || sectionConfig?.cardSubtitleLineHeight || 1.35,
                marginTop: '2px'
              }}
            >
              {card.subtitle}
            </p>
          )}
        </div>
      </a>
    );
  }

  /* ===== ESTILO B: FOTO COMPLETA CON TEXTO SUPERPUESTO (Diseño Clásico) ===== */
  return (
    <a
      href={targetLink}
      className="category-shortcut-card d-block position-relative rounded-4 overflow-hidden text-decoration-none shadow-sm h-100 border"
      style={{
        minHeight: colsNum >= 5 ? '220px' : '260px',
        height: '100%',
        backgroundColor: colorTheme === 'dark' ? '#e2e8f0' : '#1e293b',
        backgroundImage: resolvedImgUrl
          ? `url("${resolvedImgUrl}")`
          : (colorTheme === 'dark'
              ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
              : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'),
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease'
      }}
    >
      <div
        className={`position-absolute w-100 h-100 top-0 start-0 d-flex flex-column ${justifyClass} ${alignItemsClass} ${colsNum >= 5 ? 'p-3' : 'p-4'}`}
        style={{ background: overlayBg }}
      >
        <h3
          className="mb-1"
          style={{
            color: titleColor,
            fontSize: resolvedTitleFontSize,
            fontWeight: resolvedTitleFontWeight,
            textShadow: textShadowCss,
            lineHeight: card.titleLineHeight || sectionConfig?.cardTitleLineHeight || 1.2
          }}
        >
          {card.title}
        </h3>
        {card.subtitle && (
          <p
            className="mb-2"
            style={{
              color: subtitleColor,
              fontSize: resolvedSubtitleFontSize,
              fontWeight: resolvedSubtitleFontWeight,
              textShadow: textShadowCss,
              lineHeight: card.subtitleLineHeight || sectionConfig?.cardSubtitleLineHeight || 1.35
            }}
          >
            {card.subtitle}
          </p>
        )}
        {showCta && ctaText && (
          <div
            className="d-inline-flex align-items-center gap-2 fw-bold small text-uppercase"
            style={{ color: ctaColor, textShadow: textShadowCss }}
          >
            <span>{ctaText}</span>
            <i className="bi bi-arrow-right"></i>
          </div>
        )}
      </div>
    </a>
  );
};

const LandingPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(() => landingConfig ? {
    ...DEFAULT_LANDING_CONFIG,
    ...landingConfig,
    homeSections: landingConfig.homeSections || DEFAULT_HOME_SECTIONS,
    categoryShortcuts: {
      ...DEFAULT_LANDING_CONFIG.categoryShortcuts,
      ...(landingConfig.categoryShortcuts || {})
    }
  } : DEFAULT_LANDING_CONFIG);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [bestSellerProducts, setBestSellerProducts] = useState([]);

  // Sincronizar config cuando landingConfig se actualice
  useEffect(() => {
    if (landingConfig) {
      setConfig(prev => ({
        ...DEFAULT_LANDING_CONFIG,
        ...landingConfig,
        categoryShortcuts: {
          ...DEFAULT_LANDING_CONFIG.categoryShortcuts,
          ...(landingConfig.categoryShortcuts || {})
        }
      }));
    }
  }, [landingConfig]);

  // Aplicar paleta de colores de la portada al montar o al actualizar config
  useEffect(() => {
    if (storeConfig?.is_in_setup || config?.is_in_setup || storeConfig?.status === 'en_habilitacion' || config?.status === 'en_habilitacion') {
      resetSystemTheme();
      return;
    }
    const colorsToApply = config?.colors || landingConfig?.colors || storeConfig?.colors;
    if (colorsToApply && typeof window !== 'undefined' && isWebOrStoreRoute(window.location.pathname)) {
      applyEcommerceTheme(colorsToApply, { pathname: window.location.pathname });
    }
  }, [config?.colors, landingConfig?.colors, storeConfig?.colors, storeConfig?.is_in_setup, config?.is_in_setup, storeConfig?.status, config?.status]);

  // Estados de Carrito y Autenticación
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Cliente autenticado
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  // Cargar Artículos Destacados para la portada
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const featConfig = config.featuredProductsSection || {};
        const mode = featConfig.selectionMode || 'auto';
        const limit = featConfig.limit || 8;

        let params = { page: 1, page_size: limit, _t: Date.now() };

        const compId = config.company_id || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 5 : null);
        if (compId) {
          params.company = compId;
        }

        if (mode === 'manual' && featConfig.selectedProducts && featConfig.selectedProducts.length > 0) {
          const codes = featConfig.selectedProducts.map(p => p.c_artic || p.id).filter(Boolean);
          if (codes.length > 0) {
            params.ids = codes.join(',');
            params.page_size = Math.max(limit, codes.length);
          }
        } else if (mode === 'rubro' && featConfig.filterRubro) {
          params.rubro = featConfig.filterRubro;
        } else if (mode === 'ofertas' || mode === 'offers') {
          params.only_offers = 'true';
        }

        const res = await axios.get('/api/articulos/', {
          params,
          headers: compId ? { 'X-Company-ID': String(compId) } : {}
        });
        const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setFeaturedProducts(items.slice(0, limit));
      } catch (err) {
        console.warn('Error cargando artículos destacados:', err);
      }
    };
    fetchFeatured();
  }, [config.featuredProductsSection, config.company_id]);

  // Cargar Artículos "¡Los Más Vendidos!" para la portada
  useEffect(() => {
    const fetchBestSellers = async () => {
      try {
        const bsConfig = config.bestSellersSection || {};
        const mode = bsConfig.selectionMode || (bsConfig.selectedProducts?.length > 0 ? 'manual' : 'featured');
        const limit = Number(bsConfig.limit) || 8;

        let params = { page: 1, page_size: limit, _t: Date.now() };
        const compId = config.company_id || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 5 : null);
        if (compId) {
          params.company = compId;
        }

        if (mode === 'manual' && Array.isArray(bsConfig.selectedProducts) && bsConfig.selectedProducts.length > 0) {
          const codes = bsConfig.selectedProducts.map(p => p.c_artic || p.id).filter(Boolean);
          if (codes.length > 0) {
            params.ids = codes.join(',');
            params.page_size = Math.max(limit, codes.length);
          }
        } else if (mode === 'rubros' && Array.isArray(bsConfig.selectedRubros) && bsConfig.selectedRubros.length > 0) {
          params.rubro = bsConfig.selectedRubros.join(',');
        } else if (mode === 'ofertas' || mode === 'offers') {
          params.only_offers = 'true';
        }

        const res = await axios.get('/api/articulos/', {
          params,
          headers: compId ? { 'X-Company-ID': String(compId) } : {}
        });
        const items = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setBestSellerProducts(items.slice(0, limit));
      } catch (err) {
        console.warn('Error cargando artículos más vendidos:', err);
      }
    };
    fetchBestSellers();
  }, [config.bestSellersSection, config.company_id]);

  // Persistir carrito
  useEffect(() => {
    localStorage.setItem('sigecom_ecom_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const totalCartCount = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  }, [cartItems]);

  const totalCartPrice = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  }, [cartItems]);

  // Manejadores del Carrito
  const handleAddToCart = (product, quantity = 1, variant = null, size = null) => {
    setCartItems(prev => {
      const pId = product.c_artic || product.id;
      const vId = variant?.id || 'std';
      const sId = size?.nombre || 'std';
      const key = `${pId}-${vId}-${sId}`;

      const existingIndex = prev.findIndex(item => item.cartKey === key);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;
        return updated;
      }
      return [...prev, {
        ...product,
        cartKey: key,
        quantity,
        selectedVariant: variant,
        selectedSize: size,
        unitPrice: Number(product.price || product.i_unita || product.i_lista || product.i_precio || 0)
      }];
    });

    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: '¡Producto agregado al carrito!',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true
    });
  };

  const handleUpdateCartQuantity = (cartKey, newQty) => {
    if (newQty <= 0) {
      handleRemoveCartItem(cartKey);
      return;
    }
    setCartItems(prev => prev.map(item => item.cartKey === cartKey ? { ...item, quantity: newQty } : item));
  };

  const handleRemoveCartItem = (cartKey) => {
    setCartItems(prev => prev.filter(item => item.cartKey !== cartKey));
  };

  const handleClearCart = () => {
    setCartItems([]);
  };

  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const isInSetup = Boolean(
    storeConfig?.is_in_setup ||
    config?.is_in_setup ||
    storeConfig?.status === 'en_habilitacion' ||
    config?.status === 'en_habilitacion' ||
    storeConfig?.status === 'no_encontrada' ||
    config?.status === 'no_encontrada'
  );

  if (isInSetup) {
    return (
      <StoreInSetup
        storeConfig={{
          ...config,
          ...storeConfig,
          company_name: storeConfig?.company_name || landingConfig?.company_name || config?.company_name
        }}
      />
    );
  }

  return (
    <div className="landing-wrapper webstore-site-wrapper min-vh-100 d-flex flex-column" style={{ fontFamily: getFontFamilyCss(config.fontFamily || config.colors?.fontFamily) || 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* 1. Cabecera Navbar Unificada */}
      <UnifiedHeader
        activePage="home"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* SECCIONES DINÁMICAS Y REORDENABLES DEL INICIO */}
      {(() => {
        const sectionsToRender = Array.isArray(config.homeSections) && config.homeSections.length > 0
          ? config.homeSections
          : DEFAULT_HOME_SECTIONS;

        // Asegurar que todas las secciones por defecto existan si viniera una config previa
        const mergedSections = [];
        sectionsToRender.forEach(sec => {
          const def = DEFAULT_HOME_SECTIONS.find(d => d.id === sec.id);
          if (def) {
            mergedSections.push({ ...def, ...sec });
          }
        });
        DEFAULT_HOME_SECTIONS.forEach(def => {
          if (!mergedSections.some(m => m.id === def.id)) {
            mergedSections.push(def);
          }
        });

        return mergedSections.filter(sec => sec.enabled !== false).map((sec) => {
          const padClass = sec.paddingY === 'compact' ? 'py-3' : sec.paddingY === 'spacious' ? 'py-5 my-2' : sec.paddingY === 'none' ? 'py-0' : (sec.id === 'benefits' || sec.id === 'benefitsSecondary' ? 'py-4' : sec.id === 'hero' ? 'py-3' : 'py-5');
          const secFontCss = getFontFamilyCss(sec.fontFamily);
          const secFontStyle = secFontCss ? { fontFamily: secFontCss } : {};

          if (sec.id === 'hero') {
            const heroBg = sec.backgroundColor || (config.carouselSettings || storeConfig?.carouselSettings)?.backgroundColor || config.heroSectionBg || 'var(--ecom-bg, #f8fafc)';
            return (
              <section key="hero" className={padClass} style={{ backgroundColor: heroBg, ...secFontStyle }}>
                <div className={(config.carouselSettings || storeConfig?.carouselSettings)?.widthMode === 'fullwidth' ? 'container-fluid px-0' : 'container'}>
                  <UnifiedHeroBanner
                    banners={config.heroBanners || storeConfig?.banners || DEFAULT_UNIFIED_BANNERS}
                    carouselSettings={config.carouselSettings || storeConfig?.carouselSettings}
                  />
                </div>
              </section>
            );
          }

          if (sec.id === 'benefits' || sec.id === 'benefitsSecondary' || sec.id === 'benefits2') {
            const isSecBar = sec.id === 'benefitsSecondary' || sec.id === 'benefits2';
            const defList = isSecBar ? DEFAULT_LANDING_CONFIG.benefitsSecondary : DEFAULT_LANDING_CONFIG.benefits;
            const rawList = isSecBar
              ? (config.benefitsSecondary || config.benefits2 || defList)
              : (config.benefits || defList);
            const list = Array.isArray(rawList)
              ? rawList.map((b, idx) => {
                  const defItem = defList.find(d => d.id === b.id) || defList[idx] || {};
                  const hasExplicitLine3 = b.extraText !== undefined || b.line3 !== undefined || b.subtitle !== undefined;
                  return {
                    ...defItem,
                    ...b,
                    extraText: hasExplicitLine3
                      ? (b.extraText ?? b.line3 ?? b.subtitle ?? '')
                      : (defItem.extraText || '')
                  };
                })
              : [];
            if (list.length === 0) return null;
            const barSettings = isSecBar
              ? { ...DEFAULT_LANDING_CONFIG.benefitsSecondarySettings, ...(config.benefitsSecondarySettings || {}) }
              : { ...DEFAULT_LANDING_CONFIG.benefitsSettings, ...(config.benefitsSettings || {}) };
            const benefitsBg = barSettings?.backgroundColor || sec.backgroundColor || config.benefitsBackgroundColor || 'var(--ecom-bg, #f8fafc)';
            const barFontCss = getFontFamilyCss(barSettings?.fontFamily || sec?.fontFamily);
            const barFontStyle = barFontCss ? { fontFamily: barFontCss } : secFontStyle;
            const line1FontCss = getFontFamilyCss(barSettings?.line1FontFamily || barSettings?.fontFamily || sec?.fontFamily);
            const line2FontCss = getFontFamilyCss(barSettings?.line2FontFamily || barSettings?.fontFamily || sec?.fontFamily);
            const line3FontCss = getFontFamilyCss(barSettings?.line3FontFamily || barSettings?.fontFamily || sec?.fontFamily);

            const count = list.length;
            const colClass = count === 1 ? 'col-12'
              : count === 2 ? 'col-sm-6 col-12'
              : count === 3 ? 'col-lg-4 col-sm-6 col-12'
              : count === 5 ? 'col-xl col-lg-4 col-sm-6 col-12'
              : count === 6 ? 'col-xl-2 col-lg-4 col-sm-6 col-12'
              : 'col-lg-3 col-sm-6 col-12';

            return (
              <section key={sec.id} id={`seccion-${sec.id}`} className={`${padClass} border-bottom`} style={{ backgroundColor: benefitsBg, borderColor: 'var(--ecom-border, #e2e8f0)', ...barFontStyle }}>
                <div className="container">
                  <div className="row g-3 justify-content-center">
                    {list.map((b, idx) => {
                      const CardWrapper = b.link ? 'a' : 'div';
                      const linkProps = b.link ? { href: b.link, className: 'text-decoration-none d-block h-100' } : { className: 'h-100' };
                      const itemLine1Font = getFontFamilyCss(b.titleFontFamily) || line1FontCss;
                      const itemLine2Font = getFontFamilyCss(b.descFontFamily) || line2FontCss;
                      const itemLine3Font = getFontFamilyCss(b.line3FontFamily) || line3FontCss;
                      const cardBgColor = b.bgColor || b.backgroundColor || barSettings?.cardBackgroundColor || 'var(--ecom-card-bg, #ffffff)';
                      return (
                        <div key={b.id || idx} className={colClass}>
                          <CardWrapper {...linkProps}>
                            <div 
                              className={`d-flex align-items-center gap-3 p-3 rounded-4 border shadow-xs h-100 ${b.link ? 'benefit-card-clickable' : ''}`}
                              style={{ 
                                backgroundColor: cardBgColor,
                                borderColor: 'var(--ecom-border, #e2e8f0)',
                                borderRadius: 'var(--ecom-radius, 12px)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                              }}
                            >
                              <div 
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ 
                                  width: '48px', 
                                  height: '48px', 
                                  fontSize: '1.4rem',
                                  backgroundColor: b.iconBg || 'var(--ecom-primary-light, rgba(13, 110, 253, 0.12))',
                                  color: b.iconColor || 'var(--ecom-primary, #0d6efd)'
                                }}
                              >
                                <i className={`bi ${b.icon || 'bi-check-circle'}`}></i>
                              </div>
                              <div className="flex-grow-1">
                                {/* Línea 1: Título Principal */}
                                <h6 
                                  className="mb-1" 
                                  style={{ 
                                    ...(itemLine1Font ? { fontFamily: itemLine1Font } : {}),
                                    color: b.titleColor || barSettings?.line1Color || 'var(--ecom-text, #1e293b)',
                                    fontSize: resolvePilarFontSize(b.titleFontSize || barSettings?.line1FontSize, '0.95rem'),
                                    fontWeight: resolvePilarFontWeight(b.titleFontWeight || barSettings?.line1FontWeight, 700),
                                    lineHeight: 1.25
                                  }}
                                >
                                  {b.title}
                                </h6>

                                {/* Línea 2: Bajada / Descripción */}
                                {b.description && (
                                  <p 
                                    className="mb-0 lh-sm" 
                                    style={{ 
                                      ...(itemLine2Font ? { fontFamily: itemLine2Font } : {}),
                                      color: b.descColor || barSettings?.line2Color || '#64748b',
                                      fontSize: resolvePilarFontSize(b.descFontSize || barSettings?.line2FontSize, '0.84rem'),
                                      fontWeight: resolvePilarFontWeight(b.descFontWeight || barSettings?.line2FontWeight, 400)
                                    }}
                                  >
                                    {b.description}
                                  </p>
                                )}

                                {/* Línea 3: Texto Adicional / Detalle */}
                                {(b.extraText || b.line3 || b.subtitle) && (
                                  <p 
                                    className="mb-0 lh-sm mt-1" 
                                    style={{ 
                                      ...(itemLine3Font ? { fontFamily: itemLine3Font } : {}),
                                      color: b.line3Color || barSettings?.line3Color || 'var(--ecom-primary, #0d6efd)',
                                      fontSize: resolvePilarFontSize(b.line3FontSize || barSettings?.line3FontSize, '0.75rem'),
                                      fontWeight: resolvePilarFontWeight(b.line3FontWeight || barSettings?.line3FontWeight, 500)
                                    }}
                                  >
                                    {b.extraText || b.line3 || b.subtitle}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardWrapper>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          }

          if (sec.id === 'categoryShortcuts') {
            if (config.categoryShortcuts?.enabled === false || !config.categoryShortcuts?.cards?.length) return null;
            const catBg = sec.backgroundColor || config.categoryShortcuts?.backgroundColor || 'var(--ecom-bg, #f8fafc)';
            const catFontCss = getFontFamilyCss(config.categoryShortcuts?.fontFamily) || secFontCss;
            const catFontStyle = catFontCss ? { fontFamily: catFontCss } : secFontStyle;
            return (
              <section key="categoryShortcuts" className={padClass} style={{ backgroundColor: catBg, ...catFontStyle }}>
                <div className="container">
                  <div className="text-center mb-4">
                    <span 
                      className="badge fw-bold px-3 py-1 rounded-pill text-uppercase mb-2"
                      style={{
                        backgroundColor: 'var(--ecom-primary-light, rgba(13, 110, 253, 0.12))',
                        color: 'var(--ecom-primary, #0d6efd)'
                      }}
                    >
                      Líneas Principales
                    </span>
                    <h2 className="fw-bold mb-2" style={{ 
                      color: config.categoryShortcuts?.sectionTitleColor || 'var(--ecom-text, #1e293b)',
                      fontSize: getTitleFontSizeCss(config.categoryShortcuts?.titleFontSize || sec?.titleFontSize || config.titleFontSize)
                    }}>
                      {config.categoryShortcuts.sectionTitle || 'Categorías Destacadas'}
                    </h2>
                    <p className="mb-0" style={{
                      color: config.categoryShortcuts?.sectionSubtitleColor || 'var(--ecom-text-muted, #64748b)',
                      fontSize: getSubtitleFontSizeCss(config.categoryShortcuts?.subtitleFontSize || sec?.subtitleFontSize || config.subtitleFontSize)
                    }}>
                      {config.categoryShortcuts.sectionSubtitle}
                    </p>
                  </div>

                  {/* Estilo para soportar 5 columnas por fila en Bootstrap y animación hover de tarjetas */}
                  <style>{`
                    @media (min-width: 992px) {
                      .row-cols-lg-5 > * {
                        flex: 0 0 20% !important;
                        max-width: 20% !important;
                      }
                    }
                    .category-card-strip {
                      transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease !important;
                    }
                    .category-card-strip:hover {
                      transform: translateY(-4px) !important;
                      box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08) !important;
                      border-color: #94a3b8 !important;
                    }
                    .category-card-strip .category-shortcut-img {
                      transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
                    }
                    .category-card-strip:hover .category-shortcut-img {
                      transform: scale(1.06) !important;
                    }
                  `}</style>
                  {/* Grid de Accesos Rápidos */}
                  <div className={`row g-4 justify-content-center row-cols-1 row-cols-sm-2 ${
                    Number(config.categoryShortcuts?.columnsPerRow) === 6
                      ? 'row-cols-md-3 row-cols-lg-6'
                      : Number(config.categoryShortcuts?.columnsPerRow) === 5
                      ? 'row-cols-md-3 row-cols-lg-5'
                      : Number(config.categoryShortcuts?.columnsPerRow) === 3
                      ? 'row-cols-md-3 row-cols-lg-3'
                      : 'row-cols-md-3 row-cols-lg-4'
                  }`}>
                    {config.categoryShortcuts.cards.filter(c => c.is_active !== false).map((card, idx) => {
                      const colsNum = Number(config.categoryShortcuts?.columnsPerRow) || 4;
                      return (
                        <div key={card.id || idx} className="col">
                          <CategoryShortcutCard
                            card={card}
                            sectionConfig={config.categoryShortcuts}
                            colsNum={colsNum}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          }

          if (sec.id === 'bestSellers') {
            const bsCfg = config.bestSellersSection || {};
            if (bsCfg.enabled === false) return null;
            const listToRender = bestSellerProducts.length > 0 ? bestSellerProducts : featuredProducts;
            if (listToRender.length === 0) return null;
            const bsBg = sec.backgroundColor || bsCfg.backgroundColor || 'var(--ecom-bg, #f8fafc)';
            const bsCols = Number(bsCfg.columnsPerRow) || 4;
            const bsColsClass = bsCols === 6
              ? 'row-cols-md-3 row-cols-lg-6'
              : bsCols === 5
              ? 'row-cols-md-3 row-cols-lg-5'
              : bsCols === 3
              ? 'row-cols-md-3 row-cols-lg-3'
              : 'row-cols-md-3 row-cols-lg-4';

            return (
              <section key="bestSellers" id="seccion-mas-vendidos" className={`${padClass} border-top border-bottom`} style={{ backgroundColor: bsBg, borderColor: 'var(--ecom-border, #e2e8f0)', ...secFontStyle }}>
                <div className="container">
                  <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-4 gap-3">
                    <div>
                      <span
                        className="badge fw-bold px-3 py-1 rounded-pill text-uppercase mb-2"
                        style={{
                          backgroundColor: 'rgba(239, 68, 68, 0.12)',
                          color: '#dc2626'
                        }}
                      >
                        {bsCfg.badgeText || '🔥 Ranking de Ventas'}
                      </span>
                      <h2 className="fw-bold mb-1" style={{
                        color: 'var(--ecom-text, #1e293b)',
                        fontSize: getTitleFontSizeCss(bsCfg.titleFontSize || sec?.titleFontSize || config.titleFontSize)
                      }}>
                        {bsCfg.title || '¡Los Más Vendidos!'}
                      </h2>
                      <p className="text-muted mb-0" style={{
                        fontSize: getSubtitleFontSizeCss(bsCfg.subtitleFontSize || sec?.subtitleFontSize || config.subtitleFontSize)
                      }}>
                        {bsCfg.subtitle || 'Los productos más elegidos por nuestros clientes mayoristas y minoristas'}
                      </p>
                    </div>
                    <a
                      href="/tienda?mas_vendidos=1"
                      className="btn rounded-pill px-4 align-self-start align-self-md-auto fw-bold"
                      style={{
                        color: 'var(--ecom-primary, #0d6efd)',
                        borderColor: 'var(--ecom-primary, #0d6efd)',
                        borderWidth: '2px',
                        borderStyle: 'solid'
                      }}
                    >
                      Ver Todos los Más Vendidos <i className="bi bi-arrow-right ms-1"></i>
                    </a>
                  </div>

                  <div className={`row g-4 justify-content-center row-cols-1 row-cols-sm-2 ${bsColsClass}`}>
                    {listToRender.map((product) => (
                      <div key={product.c_artic || product.id} className="col">
                        <EcommerceProductCard
                          product={product}
                          onAddToCart={handleAddToCart}
                          onViewDetail={(p) => setSelectedProduct(p)}
                          onOpenDetail={(p) => setSelectedProduct(p)}
                          canSeePrices={canSeePrices}
                          imageFit={bsCfg.imageFit || 'contain'}
                          imageHeight={bsCfg.imageHeight || 'standard'}
                          onLoginClick={() => {
                            setAuthModalMode('register');
                            setIsAuthModalOpen(true);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          }

          if (sec.id === 'featuredProducts') {
            if (config.featuredProductsSection?.enabled === false || featuredProducts.length === 0) return null;
            const featBg = sec.backgroundColor || config.featuredProductsSection?.backgroundColor || 'var(--ecom-bg, #f8fafc)';
            return (
              <section key="featuredProducts" className={`${padClass} border-top border-bottom`} style={{ backgroundColor: featBg, borderColor: 'var(--ecom-border, #e2e8f0)', ...secFontStyle }}>
                <div className="container">
                  <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between mb-4 gap-3">
                    <div>
                      <span 
                        className="badge fw-bold px-3 py-1 rounded-pill text-uppercase mb-2"
                        style={{
                          backgroundColor: 'var(--ecom-accent-light, rgba(255, 107, 53, 0.14))',
                          color: 'var(--ecom-accent, #ff6b35)'
                        }}
                      >
                        Destacados del Mes
                      </span>
                      <h2 className="fw-bold mb-1" style={{ 
                        color: config.featuredProductsSection?.titleColor || 'var(--ecom-text, #1e293b)',
                        fontSize: getTitleFontSizeCss(config.featuredProductsSection?.titleFontSize || sec?.titleFontSize || config.titleFontSize)
                      }}>
                        {config.featuredProductsSection?.title || 'Artículos Destacados'}
                      </h2>
                      <p className="mb-0" style={{
                        color: config.featuredProductsSection?.subtitleColor || 'var(--ecom-text-muted, #64748b)',
                        fontSize: getSubtitleFontSizeCss(config.featuredProductsSection?.subtitleFontSize || sec?.subtitleFontSize || config.subtitleFontSize)
                      }}>
                        {config.featuredProductsSection?.subtitle}
                      </p>
                    </div>
                    {config.featuredProductsSection?.showButton !== false && Boolean((config.featuredProductsSection?.buttonText ?? 'Ver Catálogo Completo').trim()) && (
                      <a 
                        href="/tienda" 
                        className="btn rounded-pill px-4 align-self-start align-self-md-auto fw-bold"
                        style={{
                          color: 'var(--ecom-primary, #0d6efd)',
                          borderColor: 'var(--ecom-primary, #0d6efd)',
                          borderWidth: '2px',
                          borderStyle: 'solid'
                        }}
                      >
                        {(config.featuredProductsSection?.buttonText ?? 'Ver Catálogo Completo')} <i className="bi bi-arrow-right ms-1"></i>
                      </a>
                    )}
                  </div>

                  {/* Grid dinámico de Artículos Destacados según columnas configuradas */}
                  {(() => {
                    const featuredCols = Number(config.featuredProductsSection?.columnsPerRow) || 4;
                    const colsClass = featuredCols === 6
                      ? 'row-cols-md-3 row-cols-lg-6'
                      : featuredCols === 5
                      ? 'row-cols-md-3 row-cols-lg-5'
                      : featuredCols === 3
                      ? 'row-cols-md-3 row-cols-lg-3'
                      : 'row-cols-md-3 row-cols-lg-4';

                    return (
                      <div className={`row g-4 justify-content-center row-cols-1 row-cols-sm-2 ${colsClass}`}>
                        {featuredProducts.map((product) => (
                          <div key={product.c_artic || product.id} className="col">
                            <EcommerceProductCard
                              product={product}
                              onAddToCart={handleAddToCart}
                              onViewDetail={(p) => setSelectedProduct(p)}
                              onOpenDetail={(p) => setSelectedProduct(p)}
                              canSeePrices={canSeePrices}
                              imageFit={config.featuredProductsSection?.imageFit || 'contain'}
                              imageHeight={config.featuredProductsSection?.imageHeight || 'standard'}
                              onLoginClick={() => {
                                setAuthModalMode('register');
                                setIsAuthModalOpen(true);
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </section>
            );
          }

          if (sec.id === 'secondaryBanner') {
            const secondaryList = (config.secondaryBanners && config.secondaryBanners.length > 0)
              ? config.secondaryBanners
              : (DEFAULT_SECONDARY_BANNERS || []);
            const activeSecondary = secondaryList.filter(b => b.is_active !== false);
            if (activeSecondary.length === 0) return null;

            const secSettings = config.secondaryCarouselSettings || {
              sizePreset: 'standard',
              widthMode: 'container',
              fitMode: 'cover',
              borderRadius: '16px'
            };
            const secBg = sec.backgroundColor || secSettings?.backgroundColor || 'transparent';

            return (
              <section key="secondaryBanner" className={padClass} style={{ backgroundColor: secBg, ...secFontStyle }}>
                <div className={secSettings?.widthMode === 'fullwidth' ? 'container-fluid px-0' : 'container'}>
                  <UnifiedHeroBanner
                    banners={activeSecondary}
                    carouselSettings={secSettings}
                  />
                </div>
              </section>
            );
          }

          if (sec.id === 'promoBanners') {
            const activePromos = (config.promoBanners || []).filter(p => p.is_active !== false);
            if (activePromos.length === 0) return null;

            const promoSettings = config.promoBannersSettings || {
              showSectionTitle: true,
              title: 'Oportunidades & Promociones Destacadas',
              subtitle: 'Aprovechá ofertas por cantidad, lanzamientos y productos en liquidación',
              badgeText: 'Promociones Especiales',
              titleColor: '#1e293b',
              subtitleColor: '#64748b',
              cardStyle: 'overlay'
            };

            const promoBg = sec.backgroundColor || promoSettings.backgroundColor || 'var(--ecom-bg, #f8fafc)';
            return (
              <section key="promoBanners" className={padClass} style={{ backgroundColor: promoBg, ...secFontStyle }}>
                <div className="container">
                  {promoSettings.showSectionTitle !== false && (promoSettings.title || promoSettings.subtitle) && (
                    <div className="text-center mb-5 pb-1">
                      {promoSettings.badgeText && (
                        <span className="badge bg-warning-subtle text-dark fw-bold px-3 py-2 rounded-pill text-uppercase mb-3 shadow-2xs">
                          <i className="bi bi-stars me-1 text-warning"></i> {promoSettings.badgeText}
                        </span>
                      )}
                      {promoSettings.title && (
                        <h2 className="fw-black mb-2" style={{ 
                          fontWeight: 800, 
                          color: promoSettings?.titleColor || 'var(--ecom-text, #1e293b)',
                          fontSize: getTitleFontSizeCss(promoSettings?.titleFontSize || sec?.titleFontSize || config.titleFontSize) || 'calc(1.325rem + .9vw)'
                        }}>
                          {promoSettings.title}
                        </h2>
                      )}
                      {promoSettings.subtitle && (
                        <p className="mx-auto mb-0" style={{ 
                          maxWidth: '650px',
                          color: promoSettings?.subtitleColor || 'var(--ecom-text-muted, #64748b)',
                          fontSize: getSubtitleFontSizeCss(promoSettings?.subtitleFontSize || sec?.subtitleFontSize || config.subtitleFontSize) || '0.95rem'
                        }}>
                          {promoSettings.subtitle}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="row g-4 justify-content-center">
                    {activePromos.map((promo, idx, arr) => {
                      const promoCols = Number(promoSettings.columnsPerRow) || 0;
                      let colClass = 'col-12 col-lg-6';
                      if (promoCols === 1) {
                        colClass = 'col-12 col-md-11 col-lg-10';
                      } else if (promoCols === 2) {
                        colClass = 'col-12 col-md-6';
                      } else if (promoCols === 3) {
                        colClass = 'col-12 col-md-6 col-lg-4';
                      } else {
                        if (arr.length === 1) colClass = 'col-12 col-md-10 col-lg-8';
                        else if (arr.length === 3 || arr.length >= 4) colClass = 'col-12 col-md-6 col-lg-4';
                      }

                      const bannerMinHeight = promoSettings?.bannerHeight === 'compact'
                        ? '220px'
                        : promoSettings?.bannerHeight === 'large'
                        ? '360px'
                        : '290px';

                      const textAlign = promo.textAlignment || 'left';
                      const alignFlexClass = textAlign === 'center'
                        ? 'align-items-center text-center'
                        : textAlign === 'right'
                        ? 'align-items-end text-end'
                        : 'align-items-start text-start';

                      const gradientBg = textAlign === 'right'
                        ? 'linear-gradient(to left, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.65) 60%, rgba(15, 23, 42, 0.25) 100%)'
                        : textAlign === 'center'
                        ? 'linear-gradient(to bottom, rgba(15, 23, 42, 0.75) 0%, rgba(15, 23, 42, 0.85) 100%)'
                        : 'linear-gradient(to right, rgba(15, 23, 42, 0.92) 0%, rgba(15, 23, 42, 0.65) 60%, rgba(15, 23, 42, 0.25) 100%)';

                      let btnClass = 'btn-warning text-dark';
                      let btnCustomStyle = {
                        backgroundColor: 'var(--ecom-button, #ffc107)',
                        color: 'var(--ecom-button-text, #111827)',
                        border: 'none'
                      };
                      if (promo.buttonStyle === 'primary') {
                        btnClass = 'btn-primary text-white';
                        btnCustomStyle = {};
                      } else if (promo.buttonStyle === 'light') {
                        btnClass = 'btn-light text-dark';
                        btnCustomStyle = {};
                      } else if (promo.buttonStyle === 'dark') {
                        btnClass = 'btn-dark text-white';
                        btnCustomStyle = {};
                      } else if (promo.buttonStyle === 'outline-light') {
                        btnClass = 'btn-outline-light text-white';
                        btnCustomStyle = {};
                      } else if (promo.buttonStyle === 'outline-dark') {
                        btnClass = 'btn-outline-dark bg-white text-dark';
                        btnCustomStyle = {};
                      }

                      const resolvedCardStyle = (promo.cardStyle && promo.cardStyle !== 'inherit')
                        ? promo.cardStyle
                        : (promoSettings?.cardStyle || 'overlay');
                      const isBelow = resolvedCardStyle === 'below';

                      const targetLink = promo.link || (promo.rubroTarget ? `/tienda?rubro=${encodeURIComponent(promo.rubroTarget)}` : '/tienda');
                      const showButton = promo.showButton !== false && Boolean(promo.buttonText && promo.buttonText.trim());

                      const promoImgUrl = promo.image
                        ? (getImageCandidateUrls(promo.image)[0] || promo.image)
                        : null;

                      const titleColor = promo.titleColor || (isBelow ? 'var(--ecom-text, #1e293b)' : '#ffffff');
                      const subtitleColor = promo.subtitleColor || (isBelow ? '#64748b' : 'rgba(241, 245, 249, 0.95)');

                      // DISPOSICIÓN 1: Títulos Debajo de Cada Imagen (Ficha / Tarjeta)
                      if (isBelow) {
                        return (
                          <div key={promo.id || idx} className={colClass}>
                            <a
                              href={targetLink}
                              className="promo-banner-card promo-card-below d-flex flex-column h-100 text-decoration-none rounded-4 overflow-hidden border shadow-sm bg-white"
                              style={{
                                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease'
                              }}
                            >
                              {/* Imagen Superior del Banner */}
                              <div 
                                className="promo-card-img-wrapper position-relative overflow-hidden w-100 bg-light d-flex align-items-center justify-content-center"
                                style={{
                                  height: bannerMinHeight === 'compact' ? '180px' : bannerMinHeight === 'large' ? '280px' : '230px',
                                  borderBottom: '1px solid #e2e8f0'
                                }}
                              >
                                {promoImgUrl ? (
                                  <img 
                                    src={promoImgUrl} 
                                    alt={promo.title || 'Promoción'}
                                    className="w-100 h-100"
                                    style={{ objectFit: 'cover', objectPosition: 'center', transition: 'transform 0.3s ease' }}
                                  />
                                ) : (
                                  <div className="d-flex flex-column align-items-center justify-content-center text-muted py-4">
                                    <i className="bi bi-card-image fs-1 opacity-50 mb-1"></i>
                                    <span className="small opacity-75">Sin imagen</span>
                                  </div>
                                )}
                                <div className="position-absolute top-0 start-0 m-3" style={{ zIndex: 2 }}>
                                  <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase shadow-xs">
                                    <i className="bi bi-stars me-1"></i> Oportunidad Destacada
                                  </span>
                                </div>
                              </div>

                              {/* Textos y Botón Debajo de la Imagen */}
                              <div className={`p-4 d-flex flex-column flex-grow-1 justify-content-between ${alignFlexClass}`}>
                                <div>
                                  <h3 
                                    className="fw-bold mb-2" 
                                    style={{ 
                                      color: titleColor,
                                      fontSize: getTitleFontSizeCss(promo.titleFontSize) || '1.35rem',
                                      lineHeight: 1.25
                                    }}
                                  >
                                    {promo.title}
                                  </h3>
                                  {promo.subtitle && (
                                    <p 
                                      className="mb-0" 
                                      style={{ 
                                        color: subtitleColor,
                                        fontSize: getSubtitleFontSizeCss(promo.subtitleFontSize) || '0.92rem',
                                        lineHeight: 1.45
                                      }}
                                    >
                                      {promo.subtitle}
                                    </p>
                                  )}
                                </div>

                                {showButton && (
                                  <div className="mt-3 pt-2">
                                    <span 
                                      className={`btn btn-sm rounded-pill px-4 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2 ${btnClass}`}
                                      style={btnCustomStyle}
                                    >
                                      <span>{promo.buttonText}</span>
                                      <i className="bi bi-arrow-right"></i>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </a>
                          </div>
                        );
                      }

                      // DISPOSICIÓN 2: Texto Superpuesto sobre la Foto (Banner Clásico)
                      return (
                        <div key={promo.id || idx} className={colClass}>
                          <a 
                            href={targetLink}
                            className={`promo-banner-card position-relative rounded-4 overflow-hidden shadow-sm h-100 p-4 p-md-5 d-flex flex-column justify-content-between text-decoration-none text-white ${alignFlexClass}`}
                            style={{
                              minHeight: bannerMinHeight,
                              backgroundColor: '#1e293b',
                              backgroundImage: promoImgUrl
                                ? `url("${promoImgUrl}")`
                                : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                            }}
                          >
                            {(promoSettings.showGradient !== false && promo.showGradient !== false) && (
                              <div 
                                className="position-absolute top-0 start-0 w-100 h-100"
                                style={{
                                  background: gradientBg,
                                  zIndex: 1
                                }}
                              />
                            )}
                            
                            <div className="position-relative w-100" style={{ zIndex: 2 }}>
                              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-3 shadow-xs d-inline-block">
                                <i className="bi bi-stars me-1"></i> Oportunidad Destacada
                              </span>
                              <h3 
                                className="fw-black mb-2" 
                                style={{ 
                                  fontWeight: 800, 
                                  color: titleColor,
                                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                  fontSize: getTitleFontSizeCss(promo.titleFontSize) || 'calc(1.3rem + .8vw)'
                                }}
                              >
                                {promo.title}
                              </h3>
                              {promo.subtitle && (
                                <p 
                                  className="mb-0" 
                                  style={{ 
                                    maxWidth: '460px', 
                                    color: subtitleColor,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    fontSize: getSubtitleFontSizeCss(promo.subtitleFontSize) || '0.9rem',
                                    marginLeft: textAlign === 'right' ? 'auto' : textAlign === 'center' ? 'auto' : '0',
                                    marginRight: textAlign === 'center' ? 'auto' : '0'
                                  }}
                                >
                                  {promo.subtitle}
                                </p>
                              )}
                            </div>

                            {showButton && (
                              <div className="position-relative mt-4" style={{ zIndex: 2 }}>
                                <span 
                                  className={`btn btn-sm rounded-pill px-4 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2 ${btnClass}`}
                                  style={btnCustomStyle}
                                >
                                  <span>{promo.buttonText}</span>
                                  <i className="bi bi-arrow-right"></i>
                                </span>
                              </div>
                            )}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          }

          if (sec.id === 'institutional') {
            const about = config.aboutSection || {};
            if (about.enabled === false) return null;

            const badgeText = about.badgeText || 'Fábrica Directa';
            const title = about.homeTitle || about.title || 'Más de 30 años liderando en cartón, envases y embalajes';
            const description = about.paragraph1 || 'Somos una empresa argentina dedicada a la fabricación y comercialización integral de soluciones de embalaje, papelería comercial y descartables para todo el país.';
            const btn1Text = about.button1Text || 'Conocer Nuestra Empresa';
            const btn1Link = about.button1Link || '/empresa';
            const btn2Text = about.button2Text || 'Solicitar Asesoramiento';
            const btn2Link = about.button2Link || '/contacto';
            const imageUrl = about.image ? (getImageCandidateUrls(about.image)[0] || about.image) : '';
            const showFloatBadge = about.floatingBadgeShow !== false;
            const floatBadgeTitle = about.floatingBadgeTitle || 'Calidad & Stock Permanente';
            const floatBadgeSub = about.floatingBadgeSubtitle || 'Despachos rápidos a toda la Argentina';
            const secBg = sec.backgroundColor || about.backgroundColor || 'var(--ecom-bg, #f8fafc)';

            return (
              <section key="institutional" className={`${padClass} border-top border-bottom`} style={{ backgroundColor: secBg, borderColor: 'var(--ecom-border, #e2e8f0)', ...secFontStyle }}>
                <div className="container">
                  <div className="row align-items-center g-5">
                    <div className="col-lg-6 col-12">
                      {badgeText && (
                        <span 
                          className="badge fw-bold px-3 py-1 rounded-pill text-uppercase mb-2"
                          style={{
                            backgroundColor: 'var(--ecom-primary, #0d6efd)',
                            color: 'var(--ecom-primary-text, #ffffff)'
                          }}
                        >
                          {badgeText}
                        </span>
                      )}
                      <h2 className="display-6 fw-bold mb-3" style={{ 
                        color: 'var(--ecom-text, #1e293b)',
                        fontSize: getTitleFontSizeCss(about?.titleFontSize || sec?.titleFontSize || config.titleFontSize)
                      }}>
                        {title}
                      </h2>
                      <p className="text-secondary mb-3 lh-base" style={{
                        fontSize: getDescriptionFontSizeCss(about?.descriptionFontSize || sec?.bodyFontSize || config.descriptionFontSize) || '0.95rem'
                      }}>
                        {description}
                      </p>
                      <div className="d-flex flex-wrap gap-3 mt-4">
                        {btn1Text && (
                          <a 
                            href={btn1Link} 
                            className="btn rounded-pill px-4 py-2 fw-bold shadow-sm d-flex align-items-center gap-2"
                            style={{
                              backgroundColor: 'var(--ecom-primary, #0d6efd)',
                              color: 'var(--ecom-primary-text, #ffffff)',
                              border: 'none'
                            }}
                          >
                            <span>{btn1Text}</span>
                            <i className="bi bi-arrow-right"></i>
                          </a>
                        )}
                        {btn2Text && (
                          <a 
                            href={btn2Link} 
                            className="btn rounded-pill px-4 py-2 fw-semibold"
                            style={{
                              color: 'var(--ecom-text, #1e293b)',
                              borderColor: 'var(--ecom-border, #e2e8f0)',
                              backgroundColor: 'transparent',
                              borderWidth: '1px',
                              borderStyle: 'solid'
                            }}
                          >
                            {btn2Text}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="col-lg-6 col-12">
                      <div className="position-relative">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={title}
                            className="img-fluid rounded-4 shadow-lg border"
                            style={{ maxHeight: '360px', width: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div
                            className="rounded-4 border bg-light d-flex flex-column align-items-center justify-content-center text-muted"
                            style={{ height: '300px', width: '100%' }}
                          >
                            <i className="bi bi-building fs-1 opacity-50 mb-2"></i>
                            <span className="small fw-semibold">{config.logoText || 'Nuestra Empresa'}</span>
                          </div>
                        )}
                        {showFloatBadge && (floatBadgeTitle || floatBadgeSub) && (
                          <div className="position-absolute bottom-0 start-0 m-3 p-3 bg-white bg-opacity-95 rounded-3 shadow-sm border d-none d-sm-flex align-items-center gap-3">
                            <i className="bi bi-patch-check-fill text-success fs-2"></i>
                            <div>
                              {floatBadgeTitle && <h6 className="fw-bold text-dark mb-0">{floatBadgeTitle}</h6>}
                              {floatBadgeSub && <small className="text-muted">{floatBadgeSub}</small>}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          }

          if (sec.id === 'faq') {
            const faqCfg = config.faqSection || DEFAULT_FAQ_SECTION;
            if (faqCfg.enabled === false) return null;
            const whatsappNum =
              config?.contactSection?.whatsapp ||
              config?.topBar?.whatsapp ||
              storeConfig?.footer?.whatsapp ||
              config?.whatsappFloating?.phone ||
              '';
            return (
              <FaqSectionBlock
                key="faq"
                faqConfig={{
                  ...faqCfg,
                  backgroundColor: sec.backgroundColor || faqCfg.backgroundColor || 'var(--ecom-bg, #f8fafc)',
                  titleFontSize: faqCfg.titleFontSize || sec?.titleFontSize,
                  subtitleFontSize: faqCfg.subtitleFontSize || sec?.subtitleFontSize
                }}
                whatsappNumber={whatsappNum}
                padClass={padClass}
                secFontStyle={secFontStyle}
                globalTitleFontSize={config.titleFontSize}
                globalSubtitleFontSize={config.subtitleFontSize}
              />
            );
          }

          return null;
        });
      })()}

      {/* 8. FOOTER UNIFICADO */}
      <UnifiedFooter
        storeConfig={storeConfig}
        landingConfig={config}
      />

      {/* 9. MODAL DE DETALLE DE PRODUCTO */}
      {selectedProduct && (
        <EcommerceProductDetail
          show={Boolean(selectedProduct)}
          product={selectedProduct}
          onHide={() => setSelectedProduct(null)}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
          canSeePrices={canSeePrices}
          canSeePrice={canSeePrices}
          onLoginClick={() => {
            setSelectedProduct(null);
            setAuthModalMode('register');
            setIsAuthModalOpen(true);
          }}
          allArticles={featuredProducts.concat(bestSellerProducts || [])}
          onSelectProduct={(p) => setSelectedProduct(p)}
          storeConfig={storeConfig}
        />
      )}

      {/* 10. DRAWER DE CARRITO */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateCartQuantity}
        onRemoveItem={handleRemoveCartItem}
        onClearCart={handleClearCart}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
        onLoginClick={() => {
          setIsCartOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
      />

      {/* 11. MODAL DE CHECKOUT */}
      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={storeConfig?.commercial?.whatsappOrderNumber || config.topBar?.whatsapp || '5491142476622'}
        orderMessageTemplate={storeConfig?.commercial?.orderMessageTemplate}
        companyName={storeConfig?.footer?.companyName || 'HIPERPEL S.A.'}
        storeConfig={storeConfig}
        companyId={storeConfig?.company_id || 1}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
        onOrderCompleted={() => {
          setCartItems([]);
        }}
      />

      {/* 12. MODAL DE AUTENTICACIÓN / REGISTRO */}
      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={(customer) => {
          setEcomCustomer(customer);
          if (setContextCustomer) {
            setContextCustomer(customer);
          } else {
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customer));
            localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
          }
          setIsAuthModalOpen(false);
        }}
        companyId={storeConfig?.company_id || 1}
      />

      {/* 13. BOTÓN FLOTANTE DE WHATSAPP */}
      {config.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phoneNumber={config.whatsappFloating?.phone || config.topBar?.whatsapp || '5491142476622'}
          defaultMessage={config.whatsappFloating?.message || `¡Hola! Me comunico desde la portada web de ${config.logoText || 'la empresa'}.`}
          showPill={config.whatsappFloating?.showPill !== false}
          pillText={config.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        />
      )}

    </div>
  );
};

export default LandingPage;
